import React, { useContext, useEffect, useState } from "react";
import { Helmet } from 'react-helmet-async';
import { Typography, Box, Grid, Button, Card, CardContent, CardHeader, Divider, CircularProgress, Alert } from '@mui/material';
import { AuthContext } from "../components/Auth";
import FunnelCreator from "../components/FunnelCreator";
import CampaignComposer from '../components/CampaignComposer';
import DeliveryDashboard from '../components/DeliveryDashboard';
import { httpsCallable } from 'firebase/functions';

const CLIENT_ID = 'SHBnem4tYlJfMjk2bVA2eUtQZmw6MTpjaQ';
const REDIRECT_PATH = '/x';
const SCOPE = 'dm.write dm.read users.read tweet.read tweet.write offline.access';

const base64UrlEncode = (arrayBuffer) => {
    let str = '';
    const bytes = new Uint8Array(arrayBuffer);
    for (let i = 0; i < bytes.byteLength; i++) {
        str += String.fromCharCode(bytes[i]);
    }
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const sha256 = async (plain) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return await window.crypto.subtle.digest('SHA-256', data);
};

const generateRandom = (size = 32) => {
    const arr = new Uint8Array(size);
    window.crypto.getRandomValues(arr);
    let s = '';
    for (let i = 0; i < arr.byteLength; i++) s += String.fromCharCode(arr[i]);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const DashboardPage = () => {
    const { functionsInstance, authUser } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [xResult, setXResult] = useState(null);
    const [xProfile, setXProfile] = useState(null);
    const [xFetchError, setXFetchError] = useState(null);
    const [showRawToken, setShowRawToken] = useState(false);
    const [error, setError] = useState(null);
    const [funnelOpen, setFunnelOpen] = useState(false);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem('x_oauth_result');
            if (raw) {
                setXResult(JSON.parse(raw));
                // Clear after reading so it's not shown repeatedly
                sessionStorage.removeItem('x_oauth_result');
            }
        } catch (e) {
            console.warn('Failed to read x_oauth_result', e);
        }
    }, []);

    // When token result changes, fetch X (Twitter) user profile so we can show the connected account.
    // Prefer a server-side callable to avoid CORS; fall back to direct fetch if functionsInstance not available.
    useEffect(() => {
        const fetchProfile = async () => {
            setXProfile(null);
            setXFetchError(null);
            if (!xResult) return;
            try {
                const tokenData = xResult.data || xResult;
                console.debug('xResult tokenData:', tokenData);
                const accessToken = tokenData.access_token || tokenData.accessToken || tokenData.oauth_token;
                if (!accessToken) return;

                if (functionsInstance) {
                    try {
                        const fn = httpsCallable(functionsInstance, 'xProfile');
                        const resp = await fn({ access_token: accessToken });
                        console.debug('xProfile function response:', resp);
                        let body = null;
                        if (resp && resp.data) {
                            const srv = resp.data; // server-returned payload
                            if (srv.success && srv.data) {
                                // srv.data is the Twitter response; it may contain { data: { ... } }
                                body = srv.data.data ? srv.data.data : srv.data;
                            } else if (srv.data) {
                                body = srv.data;
                            } else {
                                body = srv;
                            }
                        }
                        setXProfile(body || null);
                        return;
                    } catch (err) {
                        console.warn('xProfile function call failed', err && err.message ? err.message : err);
                        setXFetchError({ message: err && err.message ? err.message : String(err) });
                        // fall through to client fetch attempt as a backup
                    }
                }

                // Fallback: direct client fetch (may be blocked by CORS)
                const resp = await fetch('https://api.twitter.com/2/users/me?user.fields=name,username,profile_image_url', {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Accept': 'application/json'
                    }
                });
                if (!resp.ok) {
                    const txt = await resp.text();
                    console.warn('Unable to fetch X profile (client)', resp.status, txt);
                    setXFetchError({ status: resp.status, text: txt });
                    return;
                }
                const body = await resp.json();
                setXProfile(body.data || body);
            } catch (e) {
                console.warn('Failed to fetch X profile', e);
                setXFetchError({ message: e && e.message ? e.message : String(e) });
            }
        };
        fetchProfile();
    }, [xResult, functionsInstance]);

    const tokenData = xResult ? (xResult.data || xResult) : null;
    const expiresReadable = (secs) => {
        if (!secs && secs !== 0) return '—';
        const s = Number(secs);
        if (isNaN(s)) return String(secs);
        if (s < 60) return `${s}s`;
        const m = Math.round(s / 60);
        if (m < 60) return `${m}m (${s}s)`;
        const h = (s / 3600).toFixed(1);
        return `${h}h (${s}s)`;
    };

    const handleConnect = async () => {
        setError(null);
        setLoading(true);
        try {
            const codeVerifier = generateRandom(64);
            const hashed = await sha256(codeVerifier);
            const codeChallenge = base64UrlEncode(hashed);
            const state = generateRandom(16);

            try {
                sessionStorage.setItem('x_oauth_code_verifier', codeVerifier);
                sessionStorage.setItem('x_oauth_state', state);
            } catch (e) {
                console.warn('Unable to access sessionStorage', e);
            }

            const redirectUri = window.location.origin + REDIRECT_PATH;
            const params = new URLSearchParams({
                response_type: 'code',
                client_id: CLIENT_ID,
                redirect_uri: redirectUri,
                scope: SCOPE,
                state: state,
                code_challenge: codeChallenge,
                code_challenge_method: 'S256'
            }).toString().replace(/\+/g, '%20');
            const oauthUrl = `https://twitter.com/i/oauth2/authorize?${params}`;
            window.location.href = oauthUrl;
        } catch (e) {
            setError(e.message || String(e));
            setLoading(false);
        }
    };

    // Start OAuth flow with option to force re-login / prompt consent
    const startOAuth = async (forceLogin = false) => {
        setError(null);
        setLoading(true);
        try {
            const codeVerifier = generateRandom(64);
            const hashed = await sha256(codeVerifier);
            const codeChallenge = base64UrlEncode(hashed);
            const state = generateRandom(16);

            try {
                sessionStorage.setItem('x_oauth_code_verifier', codeVerifier);
                sessionStorage.setItem('x_oauth_state', state);
            } catch (e) {
                console.warn('Unable to access sessionStorage', e);
            }

            const redirectUri = window.location.origin + REDIRECT_PATH;
            const params = new URLSearchParams({
                response_type: 'code',
                client_id: CLIENT_ID,
                redirect_uri: redirectUri,
                scope: SCOPE,
                state: state,
                code_challenge: codeChallenge,
                code_challenge_method: 'S256'
            }).toString().replace(/\+/g, '%20');

            // Forcing login/consent — helps user choose a different X account
            let oauthUrl = `https://twitter.com/i/oauth2/authorize?${params}`;
            if (forceLogin) {
                // `prompt=consent` is standard OAuth2; `force_login=true` may help on some X flows
                oauthUrl = oauthUrl + '&prompt=consent&force_login=true';
            }

            window.location.href = oauthUrl;
        } catch (e) {
            setError(e.message || String(e));
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Dashboard — MonadX</title>
            </Helmet>

            <Box sx={{ p: 2 }}>
                <Typography variant="h4" sx={{ mb: 2 }}>Dashboard</Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardHeader title="Account" />
                            <CardContent>
                                {authUser && authUser.user ? (
                                    <>
                                        <Typography><strong>Name:</strong> {authUser.user.displayName || '—'}</Typography>
                                        <Typography><strong>Email:</strong> {authUser.user.email || '—'}</Typography>
                                    </>
                                ) : (
                                    <Typography>No user data available.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardHeader title="X Integration" />
                            <CardContent>
                                <Typography sx={{ mb: 1 }}>Connect MonadX to your X account to enable DMs and read access.</Typography>
                                {tokenData ? (
                                    <>
                                        <Alert severity="success" sx={{ mb: 1 }}>Connected to X</Alert>
                                        {xProfile ? (
                                            <>
                                                <Typography variant="body2"><strong>X Account:</strong> {xProfile.name || xProfile.username || '—'}</Typography>
                                                <Typography variant="body2"><strong>Handle:</strong> @{xProfile.username || '—'}</Typography>
                                            </>
                                        ) : xFetchError ? (
                                            <>
                                                <Typography variant="body2"><strong>X Account:</strong> (failed to fetch)</Typography>
                                                <Typography variant="caption" color="error">{xFetchError.status ? `HTTP ${xFetchError.status}` : ''} {xFetchError.text || xFetchError.message || ''}</Typography>
                                            </>
                                        ) : (
                                            <Typography variant="body2"><strong>X Account:</strong> (fetching...)</Typography>
                                        )}
                                        <Typography variant="body2"><strong>Scope:</strong> {tokenData.scope || '—'}</Typography>
                                        <Typography variant="body2"><strong>Token Type:</strong> {tokenData.token_type || tokenData.tokenType || '—'}</Typography>
                                        <Typography variant="body2"><strong>Expires In:</strong> {expiresReadable(tokenData.expires_in)}</Typography>
                                        {tokenData.refresh_token && (
                                            <Typography variant="body2">Refresh token available</Typography>
                                        )}
                                        <Divider sx={{ my: 1 }} />
                                        <Button sx={{ mt: 1 }} variant="text" onClick={() => setShowRawToken(s => !s)}>
                                            {showRawToken ? 'Hide raw token' : 'Show raw token'}
                                        </Button>
                                        {showRawToken && tokenData && (
                                            <Box component="pre" sx={{ mt: 1, p: 1, bgcolor: '#f6f8fa', overflow: 'auto', maxHeight: 200 }}>
                                                {JSON.stringify(tokenData, null, 2)}
                                            </Box>
                                        )}
                                        <Button variant="outlined" onClick={async () => {
                                            // Clear client-side stored result so user can reconnect
                                            try { sessionStorage.removeItem('x_oauth_result'); } catch (e) { console.warn('Unable to clear x_oauth_result', e); }
                                            setXResult(null);

                                            // Optionally attempt to tell backend to disconnect (if function exists)
                                            try {
                                                if (functionsInstance) {
                                                    const xDisconnect = httpsCallable(functionsInstance, 'xDisconnect');
                                                    await xDisconnect({});
                                                }
                                            } catch (e) {
                                                // ignore if cloud function not present
                                                console.debug('xDisconnect not available or failed', e && e.message ? e.message : e);
                                            }
                                        }}>Disconnect</Button>
                                        <Button sx={{ ml: 1 }} variant="text" onClick={async () => {
                                            // Start force-login flow to let user choose a different account
                                            try { sessionStorage.removeItem('x_oauth_result'); } catch (e) {}
                                            setXResult(null);
                                            await startOAuth(true);
                                        }}>Connect another account</Button>
                                        <Button sx={{ ml: 1 }} variant="text" onClick={() => setFunnelOpen(true)}>Manage Funnels</Button>
                                        <Button sx={{ ml: 1 }} variant="text" onClick={() => {
                                            // Open X logout page in a new tab so user can sign out of their X session
                                            try {
                                                window.open('https://twitter.com/logout', '_blank', 'noopener');
                                            } catch (e) {
                                                console.warn('Unable to open X logout page', e);
                                            }
                                        }}>Sign out of X</Button>
                                    </>
                                ) : (
                                    <>
                                        <Typography sx={{ mb: 1 }}>Not connected.</Typography>
                                        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                                        <Button variant="contained" onClick={handleConnect} disabled={loading}>
                                            {loading ? <CircularProgress size={20} /> : 'Connect with X'}
                                        </Button>
                                        <Button sx={{ ml: 1 }} variant="text" onClick={() => startOAuth(true)} disabled={loading}>
                                            Connect with different account
                                        </Button>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12}>
                        <DeliveryDashboard functionsInstance={functionsInstance} />
                    </Grid>

                    <Grid item xs={12}>
                        <CampaignComposer functionsInstance={functionsInstance} />
                    </Grid>
                </Grid>
            </Box>
            <FunnelCreator open={funnelOpen} onClose={() => setFunnelOpen(false)} functionsInstance={functionsInstance} />
        </>
    )
}
export default DashboardPage;